import type { Step2Props } from "../../model/propertiesTypes";
import { useStep2ViewModel } from "../../viewmodel/useStep2ViewModel";
import styles from "./Step2.module.css";
import andyHdLogo from "@/assets/andyhdlogo.png";

export const Step2 = ({ addressData, propertyDetails, formData, onNext, onBack }: Step2Props) => {
  const {
    firstName,
    lastName,
    isSubmitting,
    addressString,
    savingsAmount,
    handleSubmit,
    handleBackClick,
  } = useStep2ViewModel({ addressData, propertyDetails, formData, onNext, onBack });

  // const maskName = (value: string) => {
  //   const v = (value ?? "").trim();
  //   if (!v) return "";
  //   if (v.length === 1) return v;
  //   return `${v[0]}${"*".repeat(v.length - 1)}`;
  // };

  return (
    <div className={styles.step2Page}>
      {/* Header Section */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={handleBackClick} aria-label="Go back">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className={styles.logoContainer}>
          <div className={styles.logoCircle}>
            <img src={andyHdLogo} alt="Andy logo" className={styles.logoImage} />
          </div>
          <span className={styles.logoText}>ANDY</span>
        </div>

        <p className={styles.description}>
          It&apos;s about you and your property, having impact your final savings results such as
          exemptions, property condition, etc.
        </p>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#10b981" stroke="#10b981" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>Estimation Savings</span>
          </div>
          <div className={`${styles.progressStep} ${styles.progressStepActive}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#10b981" stroke="#10b981" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>Owner Details</span>
          </div>
          <div className={`${styles.progressStep} ${styles.progressStepNext}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#3b82f6" stroke="#3b82f6" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>User Type</span>
          </div>
        </div>

        {/* Step Number Indicator - Large "02" */}
        <div className={styles.stepNumber}>02</div>
      </header>

      {/* Main Content */}
      <div className={styles.contentWrapper}>
        <div className={styles.contentContainer}>
          {/* Main Form Card */}
          <div className={styles.mainCard}>
            <h1 className={styles.pageTitle}>Property Owner Details</h1>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputRow}>
                <div className={styles.inputGroup}>
                  <label htmlFor="firstName" className={styles.label}>
                    Legal First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    className={styles.input}
                    placeholder="Enter First Name"
                    value={firstName}
                    readOnly
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="lastName" className={styles.label}>
                    Legal Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    className={styles.input}
                    placeholder="Enter Last Name"
                    value={lastName}
                    readOnly
                  />
                </div>
              </div>

              <div className={styles.buttonRow}>
                <button type="button" className={styles.formBackButton} onClick={handleBackClick}>
                  Back
                </button>
                <button type="submit" className={styles.continueButton} disabled={isSubmitting}>
                  Continue
                </button>
              </div>
            </form>
          </div>

          {/* Savings Card with Background SVG */}
          <div className={styles.savingsCardWrapper}>
            <div className={styles.decorativeShape}>
              <svg
                width="521"
                height="371"
                viewBox="0 0 521 371"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M438.392 0C414.109 2.24179e-06 438.8 45.6137 419.177 60.1327C399.535 74.6663 356.998 60.1327 356.998 60.1327C356.998 60.1327 399.804 73.7954 407.302 97.2967C414.796 120.783 388.088 157.429 388.088 157.429C388.088 157.429 414.119 120.265 438.392 120.265C462.665 120.265 469.051 171.943 488.696 157.429C508.342 142.916 461.989 120.783 469.482 97.2967C476.98 73.7954 527.29 83.616 519.786 60.1327C512.283 36.6494 477.249 74.6663 457.607 60.1327C437.984 45.6137 462.675 -2.24179e-06 438.392 0Z"
                  fill="#D9E5FF"
                />
                <path
                  d="M449 162C449 189.446 443.594 216.624 433.091 241.981C422.588 267.338 407.193 290.378 387.785 309.785C368.378 329.193 345.338 344.588 319.981 355.091C294.624 365.594 267.446 371 240 371C212.554 371 185.376 365.594 160.019 355.091C134.662 344.588 111.622 329.193 92.2147 309.785C72.8072 290.378 57.4124 267.338 46.9092 241.981C36.4059 216.624 31 189.446 31 162L449 162Z"
                  fill="#D9E5FF"
                />
                <path
                  d="M4.58563 160C4.58563 148.992 6.69763 140.32 10.9216 133.984C15.2096 127.648 22.0896 124.48 31.5616 124.48C41.0336 124.48 47.8816 127.648 52.1056 133.984C56.3936 140.32 58.5376 148.992 58.5376 160C58.5376 171.136 56.3936 179.872 52.1056 186.208C47.8816 192.544 41.0336 195.712 31.5616 195.712C22.0896 195.712 15.2096 192.544 10.9216 186.208C6.69763 179.872 4.58563 171.136 4.58563 160ZM42.4096 160C42.4096 153.536 41.7056 148.576 40.2976 145.12C38.8896 141.6 35.9776 139.84 31.5616 139.84C27.1456 139.84 24.2336 141.6 22.8256 145.12C21.4176 148.576 20.7136 153.536 20.7136 160C20.7136 164.352 20.9696 167.968 21.4816 170.848C21.9936 173.664 23.0176 175.968 24.5536 177.76C26.1536 179.488 28.4896 180.352 31.5616 180.352C34.6336 180.352 36.9376 179.488 38.4736 177.76C40.0736 175.968 41.1296 173.664 41.6416 170.848C42.1536 167.968 42.4096 164.352 42.4096 160ZM66.6346 182.848C68.8106 181.12 69.8026 180.32 69.6106 180.448C75.8826 175.264 80.8106 171.008 84.3946 167.68C88.0426 164.352 91.1146 160.864 93.6106 157.216C96.1066 153.568 97.3546 150.016 97.3546 146.56C97.3546 143.936 96.7466 141.888 95.5306 140.416C94.3146 138.944 92.4906 138.208 90.0586 138.208C87.6266 138.208 85.7066 139.136 84.2986 140.992C82.9546 142.784 82.2826 145.344 82.2826 148.672H66.4426C66.5706 143.232 67.7226 138.688 69.8986 135.04C72.1386 131.392 75.0506 128.704 78.6346 126.976C82.2826 125.248 86.3146 124.384 90.7306 124.384C98.3466 124.384 104.075 126.336 107.915 130.24C111.819 134.144 113.771 139.232 113.771 145.504C113.771 152.352 111.435 158.72 106.763 164.608C102.091 170.432 96.1386 176.128 88.9066 181.696H114.827V195.04H66.6346V182.848Z"
                  fill="#D7D7D7"
                />
              </svg>
            </div>
            <div className={styles.savingsCard}>
              <div className={styles.savingsContent}>
                <h2 className={styles.savingsTitle}>Great Savings</h2>
                <p className={styles.savingsAddress}>{addressString}</p>
                <div className={styles.savingsAmount}>{savingsAmount}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
